import type { IConnectionStatus } from '@/platform'
import { fetchAndroidNetworkLinkStatus } from '@/helpers/androidNetworkLinkStatus'
import { isDeviceNetworkConnected } from '@/helpers/networkLinkConnectivity'
import { isTauriRuntime } from '@/helpers/resolvedPlayerAppVersion'
import { getPlatform } from '@/platform'
import Emittery from 'emittery'

export enum NetworkStatusEvent {
  UPDATE = 'update'
}

export interface NetworkStatusEventMap {
  [NetworkStatusEvent.UPDATE]: IConnectionStatus
}

const ANDROID_LINK_POLL_MS = 5_000

function getNavigatorOnline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine
}

function mergeConnectionStatus(
  navigatorOnline: boolean,
  native: Awaited<ReturnType<typeof fetchAndroidNetworkLinkStatus>>
): IConnectionStatus {
  if (native == null) {
    return { connected: navigatorOnline, connectionType: 'unknown' }
  }
  const connected = isDeviceNetworkConnected({
    wifiEnabled: native.wifiEnabled,
    transportConnected: native.transportConnected,
    navigatorOnline
  })
  return {
    connected,
    connectionType: native.connectionType,
    wifiEnabled: native.wifiEnabled,
    transportConnected: native.transportConnected
  }
}

export default class NetworkStatusService extends Emittery<NetworkStatusEventMap> {
  private _connectionStatus: IConnectionStatus = {
    connected: getNavigatorOnline(),
    connectionType: 'unknown'
  }

  private _pollTimer: ReturnType<typeof setInterval> | null = null

  constructor() {
    super()
    void this.init()
  }

  get connectionStatus() {
    return this._connectionStatus
  }

  private async refreshNativeLinkStatus(): Promise<void> {
    const native = await fetchAndroidNetworkLinkStatus()
    const next = mergeConnectionStatus(getNavigatorOnline(), native)
    if (
      next.connected !== this._connectionStatus.connected
      || next.connectionType !== this._connectionStatus.connectionType
      || next.wifiEnabled !== this._connectionStatus.wifiEnabled
      || next.transportConnected !== this._connectionStatus.transportConnected
    ) {
      this._connectionStatus = next
      void this.emit(NetworkStatusEvent.UPDATE, this._connectionStatus)
    }
  }

  private async init() {
    window.addEventListener('online', () => {
      void this.refreshNativeLinkStatus()
    })
    window.addEventListener('offline', () => {
      void this.refreshNativeLinkStatus()
    })

    if (isTauriRuntime() && getPlatform() === 'android') {
      await this.refreshNativeLinkStatus()
      this._pollTimer = setInterval(() => {
        void this.refreshNativeLinkStatus()
      }, ANDROID_LINK_POLL_MS)
      return
    }

    this._connectionStatus = mergeConnectionStatus(getNavigatorOnline(), null)
  }
}

export type TNetworkStatusService = NetworkStatusService
